import {
  BlockTitle,
  Button,
  ListRow,
  ListRows,
  PageTitle,
  StatusTag,
  Surface,
  formatPhone,
  toast,
} from '@westhetics/ui'
import { useId } from 'react'
import { useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { BackLink, ReadingSkeleton, noteText, supportText } from '@/app/shell'
import { practitionerLine } from '@/features/appointments/labels'
import { openSignedUrl } from '@/features/documents/api'
import { errorMessage } from '@/lib/errors'
import { formatDateTimeIn } from '@/lib/format'
import { routes } from '@/routes'
import { useTreatmentPlan, useTreatmentPlanPdf } from './api'
import { NO_PDF, PDF_NOTE, photoCountText, productLine } from './labels'

/**
 * O plano de tratamento encerrado (docs/areas/portal-do-paciente.md, "Plano de tratamento";
 * RN-POR-23): os produtos aplicados com lote, validade e regiões, as etapas com quantas fotos cada
 * uma tem, o uso de imagem por extenso e a cópia dela em PDF, a mesma do e-mail do encerramento.
 * Arquétipo: leitura, no celular.
 */
export function TreatmentPlanPage() {
  const { id = '' } = useParams()
  const query = useTreatmentPlan(id)
  const pdf = useTreatmentPlanPdf()
  const reasonId = useId()

  if (query.isPending) return <ReadingSkeleton label="Carregando o plano de tratamento" />
  if (query.isError) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink to={routes.home}>Voltar ao início</BackLink>
        <LoadError
          title="Não conseguimos abrir este plano de tratamento"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      </div>
    )
  }

  const plan = query.data
  const closed = formatDateTimeIn(plan.closedAt, plan.clinic.timezone)
  const back = plan.appointmentId
    ? { to: routes.appointment(plan.appointmentId), label: 'Voltar ao atendimento' }
    : { to: routes.home, label: 'Voltar ao início' }

  return (
    <article aria-label="Plano de tratamento" className="flex flex-col gap-6">
      <BackLink to={back.to}>{back.label}</BackLink>
      <PageTitle
        eyebrow={`Plano de tratamento · ${plan.clinic.name}`}
        title={plan.title}
        meta={[
          `encerrado em ${closed.date}`,
          plan.protocolNumber
            ? `protocolo ${plan.protocolNumber}`
            : 'importado do sistema anterior',
          practitionerLine(plan.practitioner),
        ].join(' · ')}
        trailing={<StatusTag level="done">Encerrado</StatusTag>}
      />

      <div className="flex flex-col items-start gap-2">
        <Button
          disabled={!plan.hasPdf}
          aria-describedby={plan.hasPdf ? undefined : reasonId}
          loading={pdf.isPending}
          loadingLabel="Preparando…"
          onClick={() =>
            pdf.mutate(plan.id, {
              onSuccess: (link) => openSignedUrl(link.url),
              onError: (error) => toast(errorMessage(error)),
            })
          }
        >
          Baixar PDF
        </Button>
        {!plan.hasPdf ? (
          <p id={reasonId} className={noteText}>
            {NO_PDF}
          </p>
        ) : null}
      </div>

      <section aria-label="Produtos aplicados" className="flex flex-col gap-3">
        <BlockTitle title="Produtos aplicados" note={plan.summary} />
        {plan.products.length === 0 ? (
          <p className={noteText}>Nenhum produto registrado neste plano.</p>
        ) : (
          <ListRows>
            {plan.products.map((product, index) => (
              <ListRow
                key={`${index}:${product.lotNumber ?? ''}`}
                size="name"
                title={product.name ?? `Produto ${index + 1}`}
                meta={productLine(product)}
              />
            ))}
          </ListRows>
        )}
      </section>

      <section aria-label="Fotos por etapa" className="flex flex-col gap-3">
        <BlockTitle title="Fotos por etapa" note={plan.imageConsentText} />
        <ListRows>
          {plan.stages.map((stage) => (
            <ListRow
              key={stage.stage}
              title={stage.label}
              meta={photoCountText(stage.photoCount)}
            />
          ))}
        </ListRows>
      </section>

      <section aria-label="Clínica" className="flex flex-col gap-3">
        <BlockTitle title="Clínica" />
        <Surface>
          <div className="font-display text-name text-ink">{plan.clinic.name}</div>
          <p className={`${supportText} mt-1`}>{plan.clinic.address}</p>
          {plan.clinic.phone ? (
            <p className={`${supportText} mt-1`}>{formatPhone(plan.clinic.phone)}</p>
          ) : null}
        </Surface>
      </section>

      <p className={noteText}>{PDF_NOTE}</p>
    </article>
  )
}
